import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@/components/Icon';
import SkudPersonPicker from '@/components/SkudPersonPicker';
import SkudPassesBlock from '@/components/SkudPassesBlock';
import ImpersonateLinkBlock from '@/components/ImpersonateLinkBlock';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/auth/AuthContext';

/**
 * Карточка user-родителя.
 *
 * Это не «полная анкета» — паспорт/адреса/телефон лежат в `contacts`
 * (на каждого ребёнка может быть своя версия). Здесь живут только
 * глобальные поля родителя:
 *   • ФИО и email из таблицы users (как заходит в ЛК),
 *   • дети — список связей parent_students с ссылками на карточки,
 *   • человек в СКУД — привязка users.skud_person_id (одна на всех детей),
 *   • пропуска СКУД этого человека (через SkudPassesBlock).
 *
 * Личные данные правятся в карточке контакта ребёнка — туда и ссылаемся.
 */
export default function ParentEditModal({ userId, onClose, onSaved }) {
  const can = useCan();
  const canWrite = can('staff.parents', 'write');

  const [parent, setParent]   = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  // email = логин ЛК, редактируется прямо здесь (глобальное поле учётки).
  const [emailVal, setEmailVal] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const reload = async () => {
    setLoading(true); setError(null);
    try {
      const [p, s] = await Promise.all([
        api.get(`/api/parents/${userId}`),
        api.get(`/api/parents/${userId}/students`),
      ]);
      setParent(p.parent || null);
      setEmailVal(p.parent?.email || '');
      setStudents(s.rows || []);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось загрузить');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [userId]);

  const onSkudChange = async (p) => {
    if (saving || !canWrite) return;
    setSaving(true); setError(null);
    try {
      await api.put(`/api/parents/${userId}`, { skudPersonId: p?.skudId ?? null });
      await reload();
      onSaved?.();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось сохранить привязку');
    } finally {
      setSaving(false);
    }
  };

  const sp = parent?.skudPerson || null;

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay')) onClose(); }}>
      <div className="sd-modal sd-modal--medium" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>{parent?.name ? `Родитель: ${parent.name}` : 'Родитель'}</h2>
          <button className="sd-close" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>

        <div className="sd-edit__body">
          {loading && <div className="ref-empty">Загрузка…</div>}

          {error && (
            <div style={{ color: 'var(--red-fg)', padding: '8px 0' }}>{error}</div>
          )}

          {!loading && parent && (
            <>
              <div className="sd-edit__field">
                <label className="sd-edit__label">ФИО</label>
                <input className="sd-edit__input" readOnly value={parent.name || ''} />
              </div>
              <div className="sd-edit__field">
                <label className="sd-edit__label">Email (логин в ЛК)</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    className="sd-edit__input"
                    style={{ flex: 1 }}
                    value={emailVal}
                    readOnly={!canWrite}
                    onChange={(e) => { setEmailVal(e.target.value); setError(null); }}
                  />
                  {canWrite && emailVal.trim() !== (parent.email || '') && (
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      disabled={saving}
                      onClick={async () => {
                        setSaving(true); setError(null);
                        try {
                          await api.put(`/api/parents/${userId}`, { email: emailVal.trim() });
                          await reload();
                          onSaved?.();
                        } catch (e) {
                          setError(e instanceof ApiError ? e.message : 'Не удалось сохранить email');
                        } finally {
                          setSaving(false);
                        }
                      }}
                    >
                      Сохранить
                    </button>
                  )}
                </div>
              </div>
              <div className="sd-edit__field sd-edit__field--full">
                <div className="sd-edit__hint">
                  Личные данные (телефон, паспорт, адреса) правятся в карточке
                  контакта ребёнка — это поля договора и они могут отличаться
                  у одного и того же родителя для разных детей. Email и ФИО
                  контакта, привязанного к этой учётке, при сохранении контакта
                  обновляют и учётку (email — логин входа в ЛК).
                </div>
              </div>

              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label">
                  Дети {students.length ? <span className="muted">· {students.length}</span> : null}
                </label>
                {students.length === 0 ? (
                  <div className="muted" style={{ fontSize: 12.5 }}>Связей с учениками нет.</div>
                ) : (
                  <table className="table" style={{ fontSize: 12.5 }}>
                    <thead>
                      <tr>
                        <th>ФИО</th>
                        <th style={{ width: 130 }}>Роль</th>
                        <th style={{ width: 90 }}>Основной</th>
                        <th style={{ width: 100 }}>Статус</th>
                        <th style={{ width: 40 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((s) => (
                        <tr key={s.id}>
                          <td>
                            <Link
                              to={`/students?student=${s.id}`}
                              onClick={onClose}
                              style={{
                                color: s.status === 'archive' ? 'var(--fg-muted)' : 'var(--link, var(--fg))',
                                textDecoration: 'none', fontWeight: 500,
                              }}
                            >
                              {s.name}
                            </Link>
                          </td>
                          <td>{s.role || <span className="muted">—</span>}</td>
                          <td>{s.isPrimary ? '✓' : ''}</td>
                          <td>
                            {s.status === 'archive'
                              ? <span className="sb sb--archived">Архив</span>
                              : <span className="sb sb--active">Активен</span>}
                          </td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <Link
                              to={`/students?student=${s.id}`}
                              onClick={onClose}
                              title="Открыть карточку ученика"
                            >
                              <Icon name="external" size={12} />
                            </Link>
                            {canWrite && (
                              <button
                                type="button"
                                title="Отвязать ребёнка от этой учётки"
                                disabled={saving}
                                style={{
                                  marginLeft: 8, background: 'none', border: 'none',
                                  cursor: 'pointer', color: 'var(--red-fg, #dc2626)', padding: 0,
                                }}
                                onClick={async () => {
                                  if (!window.confirm(
                                    `Отвязать «${s.name}» от учётки «${parent.name}»?\n\n`
                                    + 'Родитель перестанет видеть этого ребёнка в ЛК. Контакты в карточке '
                                    + 'ребёнка останутся, отвяжется только вход в ЛК.'
                                  )) return;
                                  setSaving(true); setError(null);
                                  try {
                                    await api.delete(`/api/parents/${userId}/students/${s.id}`);
                                    await reload();
                                    onSaved?.();
                                  } catch (e2) {
                                    setError(e2 instanceof ApiError ? e2.message : 'Не удалось отвязать');
                                  } finally {
                                    setSaving(false);
                                  }
                                }}
                              >
                                <Icon name="x" size={12} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {canWrite && (
                  <LinkChildBlock
                    userId={userId}
                    existingIds={students.map((s) => s.id)}
                    onLinked={async () => { await reload(); onSaved?.(); }}
                  />
                )}
              </div>

              <ParentAccountBlock
                userId={userId}
                parent={parent}
                canWrite={canWrite}
                onChanged={reload}
              />

              {/* «Войти как» — отладка ЛК глазами родителя. Рендерится
                  только при праве staff.impersonate (внутри блока). */}
              <ImpersonateLinkBlock userId={userId} />

              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label">Человек в СКУД</label>
                <SkudPersonPicker
                  value={sp}
                  role="parent"
                  disabled={saving || !canWrite}
                  onChange={onSkudChange}
                />
                <div className="sd-edit__hint">
                  Список фильтруется по подразделению «Родители» из настроек СКУД.
                  Привязка глобальная — действует для всех детей этого родителя.
                </div>
              </div>

              {sp && (
                <div className="sd-edit__field sd-edit__field--full">
                  <label className="sd-edit__label">Пропуска СКУД</label>
                  <SkudPassesBlock skudPersonId={sp.skudId} />
                </div>
              )}
            </>
          )}
        </div>

        <div className="sd-edit__foot">
          {/* Удаление — для пустых учёток (дубли без email с рецепшена).
              С детьми/перепиской бэкенд откажет с объяснением. */}
          {can('staff.parents', 'delete') && parent && (
            <button
              type="button"
              className="btn btn--sm"
              style={{ color: 'var(--red-fg, #dc2626)' }}
              disabled={saving}
              onClick={async () => {
                if (!window.confirm(`Удалить учётку родителя «${parent.name || ''}»? Контакты в карточках детей останутся, исчезнет только вход в ЛК.`)) return;
                setSaving(true); setError(null);
                try {
                  await api.delete(`/api/parents/${userId}`);
                  onSaved?.();
                  onClose();
                } catch (e) {
                  setError(e instanceof ApiError ? e.message : 'Не удалось удалить');
                } finally {
                  setSaving(false);
                }
              }}
            >
              Удалить учётку
            </button>
          )}
          <div className="sd-edit__foot-spacer" />
          <button type="button" className="btn btn--sm" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Блок «Аккаунт ЛК» — задать пароль вручную и отправить приглашение.
 * Видим всегда (если есть право `staff.parents/write` — это уже
 * проверено родителем через `canWrite`). Кнопка приглашения отключена,
 * если у родителя не задан email — посылать письмо некуда.
 */
function ParentAccountBlock({ userId, parent, canWrite, onChanged }) {
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const [okMsg, setOkMsg]     = useState(null);

  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwd, setPwd]         = useState('');

  const [confirmInvite, setConfirmInvite] = useState(false);

  const hasEmail = !!(parent && parent.email);

  const savePassword = async () => {
    if (pwd.length < 8) { setError('Пароль должен быть не короче 8 символов'); return; }
    setBusy(true); setError(null); setOkMsg(null);
    try {
      await api.put(`/api/parents/${userId}/password`, { password: pwd });
      setOkMsg('Пароль обновлён');
      setPwd(''); setPwdOpen(false);
      onChanged && onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось сохранить пароль');
    } finally {
      setBusy(false);
    }
  };

  const sendInvite = async () => {
    setBusy(true); setError(null); setOkMsg(null);
    try {
      const res = await api.post(`/api/parents/${userId}/invite`, { resetPassword: true });
      setOkMsg(res.sent
        ? 'Приглашение отправлено. Пароль обновлён на временный (родитель сменит при первом входе).'
        : 'Письмо не удалось отправить — проверьте SMTP-настройки.');
      setConfirmInvite(false);
      onChanged && onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось отправить приглашение');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sd-edit__field sd-edit__field--full">
      <label className="sd-edit__label">Аккаунт ЛК</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
          Пароль:&nbsp;
          {parent?.hasPassword
            ? <span style={{ color: 'var(--green-700)' }}>задан</span>
            : <span style={{ color: 'var(--orange-700, #b45309)' }}>не задан</span>}
        </span>
        {canWrite && (
          <>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => { setPwdOpen((v) => !v); setError(null); setOkMsg(null); }}
              disabled={busy}
            >
              {parent?.hasPassword ? 'Сменить пароль' : 'Задать пароль'}
            </button>
            <button
              type="button"
              className="btn btn--sm btn--primary"
              onClick={() => { setConfirmInvite(true); setError(null); setOkMsg(null); }}
              disabled={busy || !hasEmail}
              title={hasEmail ? 'Сгенерировать новый временный пароль и отправить письмо на email родителя'
                              : 'Сначала заполните email родителя'}
            >
              <Icon name="mail" size={12} /> Отправить приглашение
            </button>
          </>
        )}
      </div>

      {pwdOpen && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
          <input
            type="password"
            className="sd-edit__input"
            placeholder="Новый пароль (≥8 символов)"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            style={{ maxWidth: 260 }}
          />
          <button type="button" className="btn btn--sm btn--primary" onClick={savePassword} disabled={busy}>
            Применить
          </button>
          <button type="button" className="btn btn--sm" onClick={() => { setPwdOpen(false); setPwd(''); }} disabled={busy}>
            Отмена
          </button>
        </div>
      )}

      {confirmInvite && (
        <div className="pay-inline" style={{ marginTop: 8 }}>
          <div className="pay-inline__title">Отправить письмо-приглашение?</div>
          <div className="pay-inline__hint">
            Будет сгенерирован новый временный пароль (это «обнулит» текущий, если он был),
            и письмо с шаблоном «parent_account_created» уйдёт на <b>{parent?.email}</b>.
          </div>
          <div className="pay-inline__actions">
            <button type="button" className="btn btn--sm" onClick={() => setConfirmInvite(false)} disabled={busy}>
              Отмена
            </button>
            <button type="button" className="btn btn--primary btn--sm" onClick={sendInvite} disabled={busy}>
              {busy ? 'Отправляем…' : 'Отправить'}
            </button>
          </div>
        </div>
      )}

      {error && <div style={{ color: 'var(--red-fg)', fontSize: 12.5, marginTop: 6 }}>{error}</div>}
      {okMsg && <div style={{ color: 'var(--green-700)', fontSize: 12.5, marginTop: 6 }}>{okMsg}</div>}

      {!hasEmail && (
        <div className="sd-edit__hint">
          У родителя не указан email. Без email письмо-приглашение отправить некуда.
        </div>
      )}
    </div>
  );
}

/**
 * Блок «привязать ребёнка» под таблицей детей: поиск ученика по ФИО и
 * привязка к учётке (POST /api/parents/{userId}/students). Уже привязанные
 * из результатов не показываем.
 */
function LinkChildBlock({ userId, existingIds, onLinked }) {
  const [open, setOpen]   = useState(false);
  const [q, setQ]         = useState('');
  const [rows, setRows]   = useState([]);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState(null);
  const [note, setNote]   = useState(null);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setRows([]); return undefined; }
    let aborted = false;
    const t = setTimeout(() => {
      api.get('/api/students?search=' + encodeURIComponent(q.trim()) + '&per_page=15')
        .then((res) => {
          if (aborted) return;
          setRows((res.rows || []).filter((r) => !existingIds.includes(r.id)));
        })
        .catch(() => { if (!aborted) setRows([]); });
    }, 250);
    return () => { aborted = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open]);

  const link = async (s) => {
    if (busy) return;
    setBusy(true); setErr(null); setNote(null);
    try {
      const res = await api.post(`/api/parents/${userId}/students`, { studentId: s.id });
      setQ(''); setRows([]); setOpen(false);
      if (res && res.contactLinked === false) {
        setNote('Доступ в ЛК дан. Контакта с таким же ФИО у ребёнка нет — при необходимости '
          + 'привяжите учётку к контакту в карточке ученика (карандаш у контакта).');
      }
      await onLinked?.();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Не удалось привязать');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 8 }}>
      {!open ? (
        <button type="button" className="btn btn--sm" onClick={() => { setOpen(true); setNote(null); }}>
          <Icon name="plus" size={11} /> Привязать ребёнка
        </button>
      ) : (
        <div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="sd-edit__input"
              style={{ flex: 1 }}
              autoFocus
              placeholder="Найти ученика по ФИО…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setErr(null); }}
            />
            <button type="button" className="btn btn--sm" onClick={() => { setOpen(false); setQ(''); setErr(null); }}>
              Отмена
            </button>
          </div>
          {rows.length > 0 && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 6, marginTop: 6, overflow: 'hidden' }}>
              {rows.map((r) => {
                const fio = [r.familyName, r.firstName, r.midName].filter(Boolean).join(' ');
                return (
                  <div key={r.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 10px', borderBottom: '1px solid var(--border)', fontSize: 12.5,
                  }}>
                    <span style={{ flex: 1 }}>
                      {fio}
                      {r.class?.label && <span className="muted"> · {r.class.label}</span>}
                      {r.status === 'archive' && (
                        <span className="pay-st pay-st--mute" style={{ marginLeft: 6, fontSize: 10.5 }}>архив</span>
                      )}
                    </span>
                    <button type="button" className="btn btn--primary btn--sm" disabled={busy}
                            onClick={() => link(r)}>
                      Привязать
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {q.trim().length >= 2 && rows.length === 0 && (
            <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              Никого не нашли (уже привязанные дети в поиске не показываются).
            </div>
          )}
        </div>
      )}
      {err  && <div style={{ color: 'var(--red-fg)', fontSize: 12.5, marginTop: 6 }}>{err}</div>}
      {note && <div className="sd-edit__hint" style={{ marginTop: 6 }}>{note}</div>}
    </div>
  );
}

